/**
 * Конторы в сите: полки по роду счёта, зарубежные ещё и по тиру.
 *
 * До 12.08 это была одна строка фишек по алфавиту. Владелец: «длинный
 * нечитаемый список». Алфавит сортирует по букве, человек ищет кассу, где
 * лежат деньги. С 24.09 зарубежные режутся на три тира — кто чаще кормит
 * ленту, кто реже, и все остальные. Биржи отдельной полкой: стакан, не касса.
 * «Прочих» нет: неизвестная зеркальная касса встаёт в тир 3, а не в четвёртый
 * столбец «мы не знаем».
 *
 * Порядок внутри тира 1 и 2 фиксированный, не по числу вилок в кадре: умная
 * сортировка переставляла бы кнопки под курсором. Тир 3 — хвост, сверху
 * знакомые кассы с экрана, неизвестные — по имени.
 *
 * Нажатие по названию полки отмечает её целиком: счета одного рода открывают
 * пачкой.
 *
 * Знак конторы стоит рядом с названием: тот же, что в ленте и в студии.
 */

import { isRuBook } from './mirrorDesk';
import { MIRROR_FEED } from '../lib/build';
import { EXPERIMENTS, PRIVATE_MIRROR, RU_FEED } from '../lib/feedContext';
import { type Meta, type VenueKind, venueKind } from './meta';
import type { Option } from './Pick';
import { logoOf, venueCanon } from './venues';

/** Кассы без строки в /v1/meta, но с известным родом счёта. */
const OUT_KNOWN = new Set(['stake', 'buff', 'cbet', 'egb', 'freewin']);

/** Зарубежные, которые владелец держит в первом столбце. Порядок — его. */
const MAIN_TIER1 = ['thunderpick', 'bcgame', 'duel', 'onexbet', 'cloudbet', 'csgopositive'] as const;
const TEST_TIER1 = ['cloudbet', 'pinnacle', 'bcgame', 'duel', 'jetton', 'welstroy', '1win'] as const;

/** Второй столбец: те же кассы, но реже в ленте. */
const MAIN_TIER2 = ['pinnacle', 'jetton', 'welstroy', 'ggbet', 'dexsport'] as const;
const TEST_TIER2: readonly string[] = [];

/** Биржи сверху вниз. Чужие биржи из кадра встают следом по имени. */
const MAIN_EXCHANGE = ['polymarket', 'predictfun', 'kalshi', 'sharpxch'] as const;
const TEST_EXCHANGE = ['polymarket', 'predictfun', 'orbitex'] as const;

function outTier1(): readonly string[] {
  if (PRIVATE_MIRROR) return [];
  return EXPERIMENTS ? TEST_TIER1 : MAIN_TIER1;
}

function outTier2(): readonly string[] {
  return EXPERIMENTS ? TEST_TIER2 : MAIN_TIER2;
}

function exchangeOrder(): readonly string[] {
  return EXPERIMENTS ? TEST_EXCHANGE : MAIN_EXCHANGE;
}

/** Пул /tests: фильтры кабинета + Orbitex. Без Stake/Azuro/Gamdom/Shuffle/SX. */
export const MIRROR_BOOKS = new Set([
  'cloudbet', 'pinnacle', 'bcgame', 'duel', 'jetton', 'welstroy', '1win',
  'polymarket', 'predictfun', 'betfair', 'orbitex',
]);

export const CUPIS_BOOKS = new Set(['fonbet', 'winline', 'olimp', 'leon', 'polymarket', 'kalshi']);

export function isMirrorBook(code: string): boolean {
  if (PRIVATE_MIRROR) return CUPIS_BOOKS.has(venueCanon(code));
  return MIRROR_BOOKS.has(venueCanon(code));
}

/**
 * Хвост тира 3: кассы, которые уже мелькали на экране, но не вошли в 1–2.
 * Остальные неизвестные — после них, по имени.
 */
const OUT_TIER3_HEAD = ['vbet', '5051x', 'dafabet', 'cbet', 'egb', 'stake', 'buff'] as const;


function kindOf(meta: Meta, code: string): VenueKind {
  if (OUT_KNOWN.has(venueCanon(code))) return 'out';
  return venueKind(meta, code);
}

function pickedCanon(chosen: readonly string[], code: string): boolean {
  const canon = venueCanon(code);
  return chosen.some((one) => venueCanon(one) === canon);
}

/** Два кода одной кассы — одна строка: `1win` и `onewin` не стоят рядом. */
export function collapseBooks(options: readonly Option[]): Option[] {
  const merged = new Map<string, Option>();
  for (const one of options) {
    const canon = venueCanon(one.code) || one.code;
    if (canon === '') continue;
    const prev = merged.get(canon);
    merged.set(canon, prev === undefined
      ? { ...one, code: canon }
      : { ...prev, count: prev.count + one.count });
  }
  return [...merged.values()];
}

function byOrder(order: readonly string[]) {
  return (a: Option, b: Option): number => {
    const ia = order.indexOf(venueCanon(a.code));
    const ib = order.indexOf(venueCanon(b.code));
    const ra = ia === -1 ? 1000 : ia;
    const rb = ib === -1 ? 1000 : ib;
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name, 'ru');
  };
}

export type ShelfId = 'rub' | 'out1' | 'out2' | 'out3' | 'exchange';

interface ShelfSpec {
  id: ShelfId;
  kind: VenueKind;
  name: string;
  hint: string;
  why: string;
}

/**
 * Полки в постоянном порядке. На /app рублёвых нет — четыре столбца:
 * тир 1, тир 2, тир 3, биржи.
 */
const SHELVES: ShelfSpec[] = [
  {
    id: 'rub',
    kind: 'rub',
    name: 'рублёвые',
    hint: RU_FEED ? 'счёт в рублях' : 'цупис, паспорт',
    why: RU_FEED ? 'площадки с рублёвым счётом' : 'счёт в рублях по лицензии РФ: пополняется через ЦУПИС, ставки видит налоговая',
  },
  {
    id: 'out1',
    kind: 'out',
    name: 'тир 1',
    hint: 'часто',
    why: 'зарубежные кассы, которые чаще всего кормят ленту. нажатие отмечает весь столбец',
  },
  {
    id: 'out2',
    kind: 'out',
    name: 'тир 2',
    hint: 'реже',
    why: 'те же зарубежные кассы, но строк меньше',
  },
  {
    id: 'out3',
    kind: 'out',
    name: 'тир 3',
    hint: 'остальные',
    why: 'все прочие зарубежные, кто не вошёл в тир 1 и 2',
  },
  {
    id: 'exchange',
    kind: 'exchange',
    name: 'биржи',
    hint: 'стакан, кошелёк',
    why: 'рынки предсказаний: ставка уходит в стакан к другим людям, а не против конторы, и берут её только на сумму стакана',
  },
];

export interface Shelf {
  id: ShelfId;
  kind: VenueKind;
  name: string;
  hint: string;
  why: string;
  items: Option[];
  picked: number;
}

function outTier(code: string): 1 | 2 | 3 {
  const canon = venueCanon(code);
  if (outTier1().includes(canon)) return 1;
  if (outTier2().includes(canon)) return 2;
  return 3;
}

function arrange(id: ShelfId, items: Option[]): Option[] {
  if (id === 'out1') return [...items].sort(byOrder(outTier1()));
  if (id === 'out2') return [...items].sort(byOrder(outTier2()));
  if (id === 'out3') return [...items].sort(byOrder(OUT_TIER3_HEAD));
  if (id === 'exchange') return [...items].sort(byOrder(exchangeOrder()));
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

function belongs(id: ShelfId, meta: Meta, code: string): boolean {
  const kind = kindOf(meta, code);
  if (id === 'rub') return kind === 'rub';
  if (id === 'exchange') return kind === 'exchange';
  if (kind === 'rub' || kind === 'exchange') return false;
  // `other` больше не полка: зеркальная касса без строки каталога — тир 3.
  const tier = outTier(code);
  if (id === 'out1') return tier === 1;
  if (id === 'out2') return tier === 2;
  return tier === 3;
}

/** Разложить конторы по полкам. Пустые полки не показываются вовсе. */
export function shelves(meta: Meta, options: readonly Option[], chosen: readonly string[]): Shelf[] {
  const out: Shelf[] = [];
  const collapsed = collapseBooks(options).filter((one) => !RU_FEED || isRuBook(one.code)).filter((one) => !EXPERIMENTS || isMirrorBook(one.code));
  for (const shelf of SHELVES) {
    if (MIRROR_FEED && !RU_FEED && !PRIVATE_MIRROR && shelf.id === 'rub') continue;
    const items = arrange(
      shelf.id,
      collapsed.filter((one) => belongs(shelf.id, meta, one.code)),
    );
    if (items.length === 0) continue;
    out.push({
      ...shelf,
      items,
      picked: items.filter((one) => pickedCanon(chosen, one.code)).length,
    });
  }
  return out;
}

/** Одна контора: знак, название, сколько её вилок в кадре. */
function Book({
  one,
  picked,
  main,
  onToggle,
  onEdit,
}: {
  one: Option;
  picked: boolean;
  main: boolean;
  onToggle: (code: string) => void;
  onEdit?: (code: string) => void;
}): React.ReactElement {
  const logo = logoOf(one.code);
  // Ноль вилок сейчас гасит строку, но не запрещает её: контору отмечают на
  // неделю вперёд, а лента меняется каждую секунду. Отмеченная не гаснет
  // никогда, иначе свой набор в списке не найти.
  const dim = one.count === 0 && !picked && !main;
  const button = (
    <button
      type="button"
      className={`nx-bk ${picked ? 'on' : ''} ${dim ? 'zero' : ''}${main ? ' main' : ''}`}
      aria-pressed={picked}
      title={
        one.count === 0
          ? `${one.name}: сейчас вилок нет, но отметить можно`
          : `${one.name}: ${one.count} в кадре`
      }
      onClick={() => onToggle(one.code)}
    >
      {logo === null ? (
        <span className="nx-bk__i" aria-hidden="true" />
      ) : (
        <img className="nx-bk__i" src={logo} alt="" loading="lazy" decoding="async" />
      )}
      <span className="nx-bk__n">
        {one.name}
        {main && <em className="nx-bk__main">главная</em>}
      </span>
      {one.count > 0 && <i>{one.count}</i>}
    </button>
  );
  if (onEdit === undefined) return button;
  return (
    <div className="nx-bk-w">
      {button}
      <button
        type="button"
        className="nx-bk__e"
        aria-label={`счёт в ${one.name}`}
        title={`счёт в ${one.name}: баланс, ${PRIVATE_MIRROR ? 'адрес сайта' : 'зеркало'}, прокси, пометка`}
        onClick={() => onEdit(one.code)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
        </svg>
      </button>
    </div>
  );
}

export function Books({
  kind,
  meta,
  options,
  chosen,
  mains = [],
  onToggle,
  onSet,
  onEdit,
}: {
  kind?: 'main' | 'accounts';
  meta: Meta;
  options: readonly Option[];
  chosen: readonly string[];
  mains?: readonly string[];
  onToggle: (code: string) => void;
  onSet: (codes: string[]) => void;
  onEdit?: (code: string) => void;
}): React.ReactElement | null {
  // Пустой каталог — не «все в тир 3». `venueKind` без записи сервера
  // раскладывает память вкладки по тирам наугад. Так выглядит перезагрузка:
  // шапка уже есть, полок ещё нет, через секунды каталог приезжает.
  if (Object.keys(meta.venues).length === 0) return null;
  const shelf = shelves(meta, options, chosen);
  if (shelf.length === 0) return null;

  return (
    <div className="nx-books" data-kind={kind}>
      {shelf.map((one) => {
        const all = one.picked === one.items.length;
        return (
          <section className="nx-books__sh" key={one.id}>
            {/* Название полки — кнопка: отмечает всю полку и снимает её целиком. */}
            <button
              type="button"
              className={`nx-books__t${one.picked > 0 ? ' on' : ''}`}
              title={`${one.why}. нажатие — ${all ? 'снять всю полку' : 'отметить всю полку'}`}
              onClick={() =>
                onSet(
                  all
                    ? chosen.filter((code) => !one.items.some((item) => venueCanon(item.code) === venueCanon(code)))
                    : [...new Set([...chosen.map((code) => venueCanon(code) || code), ...one.items.map((item) => item.code)])],
                )
              }
            >
              <b>{one.name}</b>
              {one.picked > 0 ? (
                <i>
                  {one.picked} из {one.items.length}
                </i>
              ) : (
                <em>{one.hint}</em>
              )}
            </button>
            {one.items.map((item) => (
              <Book
                key={item.code}
                one={item}
                picked={pickedCanon(chosen, item.code)}
                main={pickedCanon(mains, item.code)}
                onToggle={onToggle}
                onEdit={onEdit}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}
